import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { promptAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { capitalize } from '@/lib/utils';
import { FileText, Key } from 'lucide-react';
import type { PromptTemplate } from '../types';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Settings() {
  const [selectedPrompt, setSelectedPrompt] = useState<PromptTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  // Fetch current API key
  const { data: currentApiKey = '' } = useQuery({
    queryKey: ['geminiApiKey'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/keys/GEMINI_API_KEY`);
        return response.data.key_value || '';
      } catch (error) {
        console.error('Failed to fetch API key');
        return '';
      }
    },
  });

  const { data: prompts, isLoading } = useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const response = await promptAPI.list({});  // Fetch all prompts
      return response.data;
    },
  });

  // System prompt is now included in the prompts list, no need for separate query

  const handleSavePrompt = async () => {
    if (!selectedPrompt) return;
    
    try {
      await promptAPI.update(selectedPrompt.id, { prompt_template: editedPrompt });
      toast.success('Prompt updated successfully');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update prompt');
    }
  };

  const maskApiKey = (key: string) => {
    if (!key) return '';
    if (key.length <= 8) return '••••••••';
    return key.slice(0, 4) + '••••••••' + key.slice(-4);
  };

  const handleUpdateApiKey = async () => {
    if (!geminiApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/admin/keys/GEMINI_API_KEY`, {
        key_value: geminiApiKey,
        description: 'Google Gemini API Key'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success('API key updated successfully');
      setGeminiApiKey('');
      setShowApiKey(false);
    } catch (error) {
      console.error('Failed to update API key:', error);
      toast.error('Failed to update API key');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage prompts and API configuration
        </p>
      </div>

      {/* API Key Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Gemini API Key
          </CardTitle>
          <CardDescription>
            Update your Google Gemini API key for AI response generation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {currentApiKey && (
            <div className="text-sm">
              <span className="text-muted-foreground">Current key: </span>
              <code className="bg-muted px-2 py-1 rounded">
                {showApiKey ? currentApiKey : maskApiKey(currentApiKey)}
              </code>
            </div>
          )}
          <div className="flex gap-2">
            <Input
              type={showApiKey ? "text" : "password"}
              value={geminiApiKey}
              onChange={(e) => setGeminiApiKey(e.target.value)}
              placeholder="Enter new API key"
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => setShowApiKey(!showApiKey)}
            >
              {showApiKey ? 'Hide' : 'Show'}
            </Button>
            <Button
              onClick={handleUpdateApiKey}
              variant="outline"
              className="border-green-200 text-green-700 hover:bg-green-50"
            >
              Update Key
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Prompts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Prompt List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Response Templates</CardTitle>
            <CardDescription>
              AI prompts for each category
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {prompts?.map((prompt: PromptTemplate) => (
                  <div
                    key={prompt.id}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setIsEditing(false);
                    }}
                    className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${
                      selectedPrompt?.id === prompt.id ? 'bg-muted' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">
                          {capitalize(prompt.category.replace(/_/g, ' '))}
                        </span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        prompt.language === 'en' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {prompt.language.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Prompt Detail */}
        <Card className="lg:col-span-2">
          {selectedPrompt ? (
            <>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>
                    {selectedPrompt.category === 'system'
                      ? 'System Prompt'
                      : capitalize(selectedPrompt.category)}
                  </CardTitle>
                  {!isEditing && (
                    <Button
                      onClick={() => {
                        setEditedPrompt(selectedPrompt.prompt_template);
                        setIsEditing(true);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-4">
                    <textarea
                      value={editedPrompt}
                      onChange={(e) => setEditedPrompt(e.target.value)}
                      rows={16}
                      className="w-full px-4 py-3 text-sm border border-input rounded-lg focus:ring-2 focus:ring-ring bg-background font-mono resize-none"
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSavePrompt}
                        variant="outline"
                        size="sm"
                        className="border-green-200 text-green-700 hover:bg-green-50"
                      >
                        Save Changes
                      </Button>
                      <Button
                        onClick={() => setIsEditing(false)}
                        variant="outline"
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-muted/30 rounded-lg p-6 border">
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {selectedPrompt.prompt_template}
                    </pre>
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Select a prompt template to view or edit
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
